import React, { useState, useEffect, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, Outlet } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import axios from 'axios';
import ErrorBoundary from '@/components/ErrorBoundary';
import Landing from '@/pages/Landing';
import Auth from '@/pages/Auth';
import Subscription from '@/pages/Subscription';
import SubscriptionSuccess from '@/pages/SubscriptionSuccess';
import Dashboard from '@/pages/Dashboard';
import Calculator from '@/pages/Calculator';
import Troubleshooting from '@/pages/Troubleshooting';
import TroubleshootingHub from '@/pages/TroubleshootingHub';
import TroubleshootingMachine from '@/pages/TroubleshootingMachine';
import TroubleshootingSoftware from '@/pages/TroubleshootingSoftware';
import TroubleshootingSummary from '@/pages/TroubleshootingSummary';
import TroubleshootingAskAI from '@/pages/TroubleshootingAskAI';
import Knowledge from '@/pages/Knowledge';
import CuttingToolsDatabase from '@/pages/knowledge/CuttingToolsDatabase';
import ToolDetail from '@/pages/knowledge/ToolDetail';
import Community from '@/pages/Community';
import Marketplace from '@/pages/Marketplace';
import MarketplaceHome from '@/pages/MarketplaceHome';
import MarketplaceItemPage from '@/pages/MarketplaceItemPage';
import MarketplaceUploadPage from '@/pages/MarketplaceUploadPage';
import MarketplaceSuccess from '@/pages/MarketplaceSuccess';

// Legal Pages
import WhatWeDo from '@/pages/legal/WhatWeDo';
import PricingInfo from '@/pages/legal/PricingInfo';
import TermsAndPrivacy from '@/pages/legal/TermsAndPrivacy';
import SafetyDisclaimer from '@/pages/legal/SafetyDisclaimer';
import TermsPage from '@/pages/legal/TermsPage';
import PrivacyPage from '@/pages/legal/PrivacyPage';

import FeedsSpeeds from '@/pages/tools/FeedsSpeeds';
import BitSelector from '@/pages/tools/BitSelector';
import MaterialDB from '@/pages/tools/MaterialDB';
import QuickPick from '@/pages/tools/QuickPick';
import PlungeRecommender from '@/pages/tools/PlungeRecommender';
import TemplateRecommendations from '@/pages/tools/TemplateRecommendations';
import ToolsIndex from '@/pages/tools/ToolsIndex';
import GCodeMaster from '@/pages/tools/GCodeMaster';
import GCodeLibrary from '@/pages/profile/GCodeLibrary';
import PricingPrivacy from '@/pages/PricingPrivacy';
import ContactFeedback from '@/pages/ContactFeedback';
import Messages from '@/pages/Messages';
import Workflow from '@/pages/Workflow';
import MyStuff from '@/pages/MyStuff';


import TermsOfService from '@/pages/TermsOfService';
import Introduction from '@/pages/Introduction';
import Profile from '@/pages/Profile';
import MachineSetup from '@/pages/MachineSetup';
import MachineSetupWizard from '@/pages/MachineSetupWizard';
import ProjectTracker from '@/pages/ProjectTracker';
import Layout from '@/components/Layout';
import OnboardingWizard from '@/pages/OnboardingWizard';
import OnboardingModal from '@/components/OnboardingModal';
import AdminDashboard from '@/pages/AdminDashboard';
import Methodology from '@/pages/Methodology';

// Admin Module
import AdminLanding from '@/pages/admin/AdminLanding';
import UserManagement from '@/pages/admin/UserManagement';

// 3D Printing Module
import Printing3DDashboard from '@/pages/printing3d/Printing3DDashboard';
import PrintCalculator from '@/pages/printing3d/PrintCalculator';
import PrintTroubleshooting from '@/pages/printing3d/PrintTroubleshooting';
import MaterialsGuide from '@/pages/printing3d/MaterialsGuide';
import PrinterSetupWizard from '@/pages/printing3d/PrinterSetupWizard';
import Printing3DAdmin from '@/pages/printing3d/Printing3DAdmin';
import Print3DAskAI from '@/pages/printing3d/Print3DAskAI';
import Print3DIntroduction from '@/pages/printing3d/Print3DIntroduction';
import Printing3DMarketplace from '@/pages/printing3d/Printing3DMarketplace';
import Print3DMethodology from '@/pages/printing3d/Print3DMethodology';
import Print3DEquipment from '@/pages/printing3d/Print3DEquipment';
import Print3DProjectPicker from '@/pages/printing3d/Print3DProjectPicker';
import Print3DTools from '@/pages/printing3d/Print3DTools';
import PostProcessing from '@/pages/printing3d/PostProcessing';
import SlicerGuide from '@/pages/printing3d/SlicerGuide';
import Print3DKnowledge from '@/pages/printing3d/Print3DKnowledge';

// Laser Cutting Module
import LaserDashboard from '@/pages/laser/LaserDashboard';
import LaserIntroduction from '@/pages/laser/LaserIntroduction';
import LaserCalculator from '@/pages/laser/LaserCalculator';
import LaserTroubleshooting from '@/pages/laser/LaserTroubleshooting';
import LaserSetupWizard from '@/pages/laser/LaserSetupWizard';
import LaserMaterialsGuide from '@/pages/laser/LaserMaterialsGuide';
import LaserAskAI from '@/pages/laser/LaserAskAI';
import LaserAdmin from '@/pages/laser/LaserAdmin';
import LaserMarketplace from '@/pages/laser/LaserMarketplace';
import LaserMethodology from '@/pages/laser/LaserMethodology';
import LaserEquipment from '@/pages/laser/LaserEquipment';
import LaserProjectPicker from '@/pages/laser/LaserProjectPicker';
import LaserTools from '@/pages/laser/LaserTools';
import LaserSafety from '@/pages/laser/LaserSafety';
import LaserDesignPrep from '@/pages/laser/LaserDesignPrep';
import LaserSoftwareGuide from '@/pages/laser/LaserSoftwareGuide';
import LaserKnowledge from '@/pages/laser/LaserKnowledge';
import LaserMachinesCatalog from '@/pages/laser/LaserMachinesCatalog';

// Community Features (Game-Changer)
import CommunitySettings from '@/components/CommunitySettings';

import { getActiveModule, clearActiveModule, isActiveModuleValid } from '@/utils/activeModule';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

// Sends non-CNC users from /dashboard to their own module dashboard.
function DashboardRouter() {
  const module = getActiveModule();
  if (module === 'laser_cutting') return <Navigate to="/laser" replace />;
  if (module === '3d_printing') return <Navigate to="/3d-printing" replace />;
  return <Dashboard />;
}

// Guards a group of routes by module access. Renders <Outlet /> if allowed, redirects otherwise.
function RequireModuleOutlet({ module: moduleName }) {
  const { user } = useContext(AuthContext);
  if (!user) return <Navigate to="/auth" replace />;
  // Admins and super-admins bypass all module locks
  if (user.is_super_admin || user.is_admin) return <Outlet />;
  // Session lock: the active module chosen at login must match this route's module
  const activeModule = getActiveModule();
  if (activeModule !== null && activeModule !== moduleName) return <Navigate to="/" replace />;
  const disciplineAccess = user.discipline_access || {};
  if (disciplineAccess[moduleName]) return <Outlet />;
  return <Navigate to={`/?module=${moduleName}`} replace />;
}

// Component to handle /auth route - shows Auth page if reset token present, even when logged in
function AuthRouteHandler({ user, hasMachineProfile }) {
  const [searchParams] = useSearchParams();
  const hasResetToken = searchParams.has('reset');
  // Check both URL param and active session module for discipline
  const discipline = searchParams.get('discipline') || getActiveModule();
  
  console.log('AuthRouteHandler - hasResetToken:', hasResetToken, 'user:', !!user, 'discipline:', discipline);
  
  // If there's a reset token, always show the Auth page (for password reset)
  if (hasResetToken) {
    return <Auth />;
  }
  
  // Otherwise, redirect logged-in users based on discipline
  if (user) {
    if (discipline === '3d_printing') {
      return <Navigate to="/3d-printing" replace />;
    } else if (discipline === 'laser_cutting') {
      return <Navigate to="/laser" replace />;
    } else if (discipline === 'admin') {
      // Only redirect to admin panel if the logged-in user is actually an admin.
      // Non-admin users land here when clicking "Admin Login" while already logged in —
      // show the login form so they can enter admin credentials instead of hitting Access Denied.
      if (user.is_admin || user.is_super_admin) {
        return <Navigate to="/admin" replace />;
      }
      return <Auth />;
    } else {
      // Default to CNC Router
      return hasMachineProfile ? <Navigate to="/dashboard" replace /> : <Navigate to="/settings/machine-setup" replace />;
    }
  }
  
  return <Auth />;
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [hasMachineProfile, setHasMachineProfile] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showFirstTimeOnboarding, setShowFirstTimeOnboarding] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      try {
        // 1) Validate token and fetch user
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);

        // Validate stored active module is still valid for this user; clear if stale
        const storedModule = getActiveModule();
        if (storedModule && !isActiveModuleValid(storedModule, response.data)) {
          clearActiveModule();
        }

        // Check if first-time onboarding modal is needed (strict false check — undefined = existing user = skip)
        if (response.data.onboarding_complete === false) {
          setShowFirstTimeOnboarding(true);
        }

        // 2) Check if onboarding is needed (only for CNC Routing discipline)
        // Skip onboarding wizard for 3D Printing and Laser - they have their own setup pages
        const activeDiscipline = getActiveModule();
        if (activeDiscipline === 'cnc_routing') {
          try {
            const onboardingRes = await axios.get(`${API}/onboarding/check-status`);
            const onboardingNeeded = onboardingRes.data?.needs_onboarding;
            setNeedsOnboarding(onboardingNeeded);
            if (onboardingNeeded) {
              setShowOnboarding(true);
            }
          } catch (e) {
            console.error('Failed to check onboarding status:', e);
          }
        }

        // 3) Check if a machine profile exists
        try {
          const profileRes = await axios.get(`${API}/machines/has-profile`);
          setHasMachineProfile(Boolean(profileRes.data?.has_profile));
        } catch (e) {
          console.error('Failed to check machine profile status:', e);
          setHasMachineProfile(false);
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    run();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUser = async () => {
    // Kept for compatibility with any existing calls, but not used in useEffect anymore
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    clearActiveModule();
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    // Navigate to landing page after logout
    window.location.href = '/';
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    setNeedsOnboarding(false);
  };

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
    // User can still access app, but needsOnboarding stays true for "Update Setup" prompt
  };

  const handleFirstTimeOnboardingComplete = () => {
    setShowFirstTimeOnboarding(false);
    setShowOnboarding(false);    // prevent legacy wizard from firing now that the guard flag is cleared
    setNeedsOnboarding(false);   // keep needsOnboarding consistent
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-xl text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Show onboarding wizard for returning users who haven't completed CNC setup.
  // New users (showFirstTimeOnboarding=true) bypass this and go straight to OnboardingModal.
  if (user && showOnboarding && !showFirstTimeOnboarding) {
    return (
      <AuthContext.Provider value={{ user, token, login, logout, needsOnboarding, setShowOnboarding }}>
        <BrowserRouter>
          <OnboardingWizard 
            onComplete={handleOnboardingComplete} 
            onSkip={handleOnboardingSkip} 
          />
        </BrowserRouter>
        <Toaster />
      </AuthContext.Provider>
    );
  }

  return (
    <ErrorBoundary>
      <AuthContext.Provider value={{ user, token, login, logout, needsOnboarding, setShowOnboarding }}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            
            {/* Legal/Info Pages (Public) */}
            <Route path="/what-we-do" element={<WhatWeDo />} />
            <Route path="/pricing" element={<PricingInfo />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms-combined" element={<TermsAndPrivacy />} />
            <Route path="/safety" element={<SafetyDisclaimer />} />
            
            <Route
              path="/auth"
              element={
                <AuthRouteHandler user={user} hasMachineProfile={hasMachineProfile} />
              }
            />
            <Route
              path="/account/login"
              element={
                user ? (
                  hasMachineProfile ? <Navigate to="/dashboard" /> : <Navigate to="/settings/machine-setup" />
                ) : (
                  <Auth />
                )
              }
            />
            <Route
              path="/account/register"
              element={
                user ? (
                  hasMachineProfile ? <Navigate to="/dashboard" /> : <Navigate to="/settings/machine-setup" />
                ) : (
                  <Auth initialTab="register" />
                )
              }
            />
            <Route path="/account/subscription" element={user ? <Subscription /> : <Navigate to="/auth" />} />
            <Route path="/subscription" element={user ? <Subscription /> : <Navigate to="/auth" />} />
            <Route path="/subscription/success" element={user ? <SubscriptionSuccess /> : <Navigate to="/auth" />} />
            
            {/* Admin Routes - Outside Layout to avoid discipline sidebar */}
            <Route path="/admin" element={user ? <AdminLanding /> : <Navigate to="/auth" />} />
            <Route path="/admin/cnc-routing" element={user ? <AdminDashboard /> : <Navigate to="/auth" />} />
            <Route path="/admin/3d-printing" element={user ? <Printing3DAdmin /> : <Navigate to="/auth" />} />
            <Route path="/admin/laser" element={user ? <AdminDashboard /> : <Navigate to="/auth" />} />
            <Route path="/admin/users" element={user ? <UserManagement /> : <Navigate to="/auth" />} />
            <Route path="/admin/applications" element={user ? <AdminDashboard /> : <Navigate to="/auth" />} />
            <Route path="/admin/settings" element={user ? <AdminDashboard /> : <Navigate to="/auth" />} />
            <Route path="/laser/admin" element={user ? <LaserAdmin /> : <Navigate to="/auth" />} />
            
            <Route
              path="/*"
              element={
                user ? (
                  <Layout>
                    <Routes>
                      <Route path="/dashboard" element={<DashboardRouter />} />
                      <Route path="/my-stuff" element={<MyStuff />} />
                      <Route path="/calculator" element={<Calculator />} />
                      <Route path="/tools" element={<ToolsIndex />} />
                      <Route path="/tools/feeds-speeds" element={<FeedsSpeeds />} />
                      <Route path="/tools/gcode-master" element={<GCodeMaster />} />
                      <Route path="/tools/bit-selector" element={<BitSelector />} />
                      <Route path="/tools/material-db" element={<MaterialDB />} />
                      <Route path="/tools/quick-pick" element={<QuickPick />} />
                      <Route path="/tools/plunge-recommender" element={<PlungeRecommender />} />
                      <Route path="/tools/templates" element={<TemplateRecommendations />} />

                      {/* Troubleshooting hub + ask-ai */}
                      <Route path="/troubleshooting" element={<TroubleshootingHub />} />
                      <Route path="/troubleshooting/ask-ai" element={<TroubleshootingAskAI />} />
                      <Route path="/troubleshooting/machine" element={<TroubleshootingMachine />} />
                      <Route path="/troubleshooting/software" element={<TroubleshootingSoftware />} />
                      <Route path="/troubleshooting/summary" element={<TroubleshootingSummary />} />
                      <Route path="/knowledge" element={<Knowledge />} />
                      <Route path="/knowledge/tools" element={<CuttingToolsDatabase />} />
                      <Route path="/knowledge/tools/:id" element={<ToolDetail />} />
                      <Route path="/methodology" element={<Methodology />} />
                      <Route path="/introduction" element={<Introduction />} />
                      <Route path="/community" element={<Community />} />
                      
                      {/* Community Settings - Game Changer Feature */}
                      <Route path="/community-settings" element={<CommunitySettings discipline="cnc_routing" />} />
                      
                      <Route path="/marketplace" element={<MarketplaceHome />} />
                      <Route path="/marketplace/item/:id" element={<MarketplaceItemPage />} />
                      <Route path="/marketplace/upload" element={<MarketplaceUploadPage />} />
                      <Route path="/marketplace/success" element={<MarketplaceSuccess />} />

                      <Route path="/pricing-privacy" element={<PricingPrivacy />} />
                      <Route path="/workflow" element={<Workflow />} />
                      <Route path="/messages" element={<Messages />} />
                      <Route path="/contact-feedback" element={<ContactFeedback />} />
                      <Route path="/terms-of-service" element={<TermsOfService />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/profile/gcode-library" element={<GCodeLibrary />} />
                      <Route path="/settings/machine-setup" element={<MachineSetup />} />
                      <Route path="/setup-wizard" element={<MachineSetupWizard />} />
                      <Route path="/projects" element={<ProjectTracker />} />
                      
                      {/* 3D Printing Module — requires 3d_printing access */}
                      <Route element={<RequireModuleOutlet module="3d_printing" />}>
                        <Route path="/3d-printing" element={<Printing3DDashboard />} />
                        <Route path="/3d-printing/introduction" element={<Print3DIntroduction />} />
                        <Route path="/3d-printing/calculator" element={<PrintCalculator />} />
                        <Route path="/3d-printing/troubleshooting" element={<PrintTroubleshooting />} />
                        <Route path="/3d-printing/ask-ai" element={<Print3DAskAI />} />
                        <Route path="/3d-printing/materials" element={<MaterialsGuide />} />
                        <Route path="/3d-printing/setup" element={<PrinterSetupWizard />} />
                        <Route path="/3d-printing/my-equipment" element={<Print3DEquipment />} />
                        <Route path="/3d-printing/project-picker" element={<Print3DProjectPicker />} />
                        <Route path="/3d-printing/my-tools" element={<Print3DTools />} />
                        <Route path="/3d-printing/marketplace" element={<Printing3DMarketplace />} />
                        <Route path="/3d-printing/methodology" element={<Print3DMethodology />} />
                        <Route path="/3d-printing/post-processing" element={<PostProcessing />} />
                        <Route path="/3d-printing/slicer-guide" element={<SlicerGuide />} />
                        <Route path="/3d-printing/knowledge" element={<Print3DKnowledge />} />
                        <Route path="/3d-printing/community-settings" element={<CommunitySettings discipline="3d_printing" />} />
                        <Route path="/3d-printing/profile" element={<Profile />} />
                        <Route path="/3d-printing/profile/gcode-library" element={<GCodeLibrary />} />
                        <Route path="/3d-printing/messages" element={<Messages />} />
                      </Route>

                      {/* Laser Cutting Module — requires laser_cutting access */}
                      <Route element={<RequireModuleOutlet module="laser_cutting" />}>
                        <Route path="/laser" element={<LaserDashboard />} />
                        <Route path="/laser/introduction" element={<LaserIntroduction />} />
                        <Route path="/laser/calculator" element={<LaserCalculator />} />
                        <Route path="/laser/troubleshooting" element={<LaserTroubleshooting />} />
                        <Route path="/laser/setup" element={<LaserSetupWizard />} />
                        <Route path="/laser/my-equipment" element={<LaserEquipment />} />
                        <Route path="/laser/project-picker" element={<LaserProjectPicker />} />
                        <Route path="/laser/my-tools" element={<LaserTools />} />
                        <Route path="/laser/materials" element={<LaserMaterialsGuide />} />
                        <Route path="/laser/ask-ai" element={<LaserAskAI />} />
                        <Route path="/laser/marketplace" element={<LaserMarketplace />} />
                        <Route path="/laser/methodology" element={<LaserMethodology />} />
                        <Route path="/laser/community-settings" element={<CommunitySettings discipline="laser_cutting" />} />
                        <Route path="/laser/profile" element={<Profile />} />
                        <Route path="/laser/profile/gcode-library" element={<GCodeLibrary />} />
                        <Route path="/laser/messages" element={<Messages />} />
                        <Route path="/laser/safety" element={<LaserSafety />} />
                        <Route path="/laser/design-prep" element={<LaserDesignPrep />} />
                        <Route path="/laser/software-guide" element={<LaserSoftwareGuide />} />
                        <Route path="/laser/knowledge" element={<LaserKnowledge />} />
                        <Route path="/laser/machines" element={<LaserMachinesCatalog />} />
                      </Route>
                    </Routes>
                  </Layout>
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
          </Routes>
          {user && showFirstTimeOnboarding && (
            <OnboardingModal onComplete={handleFirstTimeOnboardingComplete} />
          )}
        </BrowserRouter>
        <Toaster />
      </AuthContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
